// src/components/financials/FundBalanceDashboard.js
// Updated: 01/25/2026 - Fixed Double Counting of Opening Balances

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, 
  TableHead, TableRow, Grid, Card, CardContent, CircularProgress,
  Button, Alert, LinearProgress
} from '@mui/material';
import { 
  AccountBalance, TrendingUp, TrendingDown, Sync 
} from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where, writeBatch, doc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const FundBalanceDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fundStats, setFundStats] = useState([]);
  const [totals, setTotals] = useState({ income: 0, expense: 0, balance: 0 });
  const [syncing, setSyncing] = useState(false);

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (!churchId) return;
    calculateBalances();
  }, [churchId]);

  const calculateBalances = async () => {
    setLoading(true);
    try {
      // 1. Get Funds (Definitions)
      const fundsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
      const fundsMap = {};
      fundsSnap.docs.forEach(doc => {
        const d = doc.data();
        fundsMap[doc.id] = { 
          id: doc.id, 
          name: d.name, 
          isRestricted: d.isRestricted,
          starting: parseFloat(d.startingBalance) || 0,
          income: 0, 
          expense: 0 
        };
      });

      // 2. Get Money IN (Posted Transactions Only)
      const incomeQ = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'transactions'),
        where('status', '==', 'posted')
      );
      const incomeSnap = await getDocs(incomeQ);
      incomeSnap.docs.forEach(doc => {
        const data = doc.data();

        // --- FIX: Prevent Double Counting Opening Balances ---
        // We skip 'opening_balance' types here because they are already 
        // accounted for in the 'starting' field loaded in Step 1.
        if (data.type === 'opening_balance') return; 

        // Handle Splits or Single Fund
        if (data.splits && data.splits.length > 0) {
          data.splits.forEach(split => {
            if (fundsMap[split.fundId]) {
              fundsMap[split.fundId].income += (Number(split.amount) || 0);
            }
          });
        } else if (data.fundId && fundsMap[data.fundId]) {
          fundsMap[data.fundId].income += (Number(data.amount) || 0); 
        }
      });

      // 3. Get Money OUT (Approved Expenses Only)
      const expenseQ = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'expenses'),
        where('status', '==', 'approved')
      );
      const expenseSnap = await getDocs(expenseQ);
      expenseSnap.docs.forEach(doc => {
        const data = doc.data();
        if (data.fundId && fundsMap[data.fundId]) {
          fundsMap[data.fundId].expense += (Number(data.amount) || 0);
        }
      });

      // 4. Calculate Net (Starting + Income - Expense)
      let totalIncome = 0;
      let totalExpense = 0;
      let totalStarting = 0;

      const stats = Object.values(fundsMap).map(f => {
        const balance = f.starting + f.income - f.expense; 
        totalIncome += f.income;
        totalExpense += f.expense;
        totalStarting += f.starting;
        return { ...f, balance };
      });

      setFundStats(stats);
      setTotals({ 
        income: totalIncome, 
        expense: totalExpense, 
        balance: totalStarting + totalIncome - totalExpense
      });

    } catch (e) {
      console.error("Balance Calc Error:", e);
    }
    setLoading(false);
  };

  // --- SYNC FUNCTION ---
  const handleSyncBalances = async () => {
    setSyncing(true);
    try {
      const batch = writeBatch(db);
      fundStats.forEach(fund => {
        const fundRef = doc(db, 'churches', churchId, 'financials', 'data', 'funds', fund.id);
        batch.update(fundRef, { 
          balance: fund.balance,
          lastCalculatedAt: new Date()
        });
      });
      await batch.commit();
      alert("Balances synchronized successfully!");
    } catch (e) {
      console.error(e);
      alert("Sync failed.");
    }
    setSyncing(false);
  };

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 5 }} />;

  return (
    <Box sx={{ mt: 2 }}>
      {/* SUMMARY CARDS */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Total Income (YTD)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h4" color="success.main">${totals.income.toLocaleString()}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Total Expenses (YTD)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingDown sx={{ color: 'error.main', mr: 1 }} />
                <Typography variant="h4" color="error.main">-${totals.expense.toLocaleString()}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Total Available (Net)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalance sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h4" color="primary.main">${totals.balance.toLocaleString()}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ACTION BAR */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Fund Performance Breakdown</Typography>
        <Button 
          variant="outlined" 
          startIcon={syncing ? <CircularProgress size={16} /> : <Sync />} 
          onClick={handleSyncBalances}
          disabled={syncing}
        >
          {syncing ? "Updating..." : "Sync Database Balances"}
        </Button>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Click "Sync" to update the available balance visible in the Expense Approval Queue.
      </Alert>

      {/* DETAILED TABLE */}
      <Paper>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Fund Name</TableCell>
              <TableCell align="right">Starting</TableCell>
              <TableCell align="right">Income</TableCell>
              <TableCell align="right">Expenses</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell align="right">Health</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fundStats.map((fund) => (
              <TableRow key={fund.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{fund.name}</Typography>
                  {fund.isRestricted && <Typography variant="caption" color="warning.main">Restricted</Typography>}
                </TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                  ${fund.starting.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>
                  +${fund.income.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  -${fund.expense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  ${fund.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </TableCell>
                <TableCell align="right" sx={{ width: 150 }}>
                   {(fund.starting + fund.income) > 0 ? (
                     <LinearProgress 
                       variant="determinate" 
                       value={Math.min(100, Math.max(0, (fund.balance / (fund.starting + fund.income)) * 100))} 
                       color={fund.balance < 0 ? "error" : "primary"}
                       sx={{ height: 8, borderRadius: 5 }}
                     />
                   ) : (
                     <Typography variant="caption" color="textSecondary">-</Typography>
                   )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default FundBalanceDashboard;